import { StyleSheet, View } from 'react-native';

import { DatetimeShow, ScrollView, XStack, tokens } from '@fhs/ui';
import Footer from '@fhs-legacy/frontend/src/components/footer';
import { region } from '@fhs-legacy/frontend/src/utils/environment';

import {
  firstRespondersMonthEndDate,
  firstRespondersMonthStartDate,
  hotSauceBarCanadaEndDate,
  hotSauceBarCanadaStartDate,
  montrealSmokedMeatCAEndDate,
  montrealSmokedMeatCAStartDate,
} from './constants';
import { HomeAlert } from './home-alert';
import { HomeCard } from './home-card';
import { LtoContentCA } from './lto-content/lto-content-ca';
import { LtoContentUS } from './lto-content/lto-content-us';
import {
  CareersCard,
  CateringCard,
  FirstRespondersMonthCard,
  FrenchDipCard,
  HookAndLadderCard,
  HotSauceBarCard,
  MontrealSmokedMeatCard,
  PublicSafetyFoundationCard,
} from './marketing-cards-desktop';
import { HotSauceBarCanadaCard } from './marketing-cards-desktop/hot-sauce-bar-canada-card';
import { ThanksgivingTurkeySubCard } from './marketing-cards-desktop/thanksgiving-turkey-sub-card';
import { TripleStackCard } from './marketing-cards-desktop/triple-stack';
import { WelcomeCard } from './marketing-cards-desktop/welcome-card/welcome-card';
import { ScreenHomeProps } from './types';

function MarketingCards({
  cateringHref,
  pickUpHref,
  foundationHref,
  careersHref,
  deliveryHref,
}: {
  cateringHref: string;
  pickUpHref: string;
  foundationHref: string;
  careersHref: string;
  donateHref: string;
  deliveryHref: string;
}) {
  return (
    <>
      <WelcomeCard pickUpHref={pickUpHref} deliveryHref={deliveryHref} />
      {region() === 'US' ? (
        <LtoContentUS
          default={null}
          tripleStack={<TripleStackCard url={pickUpHref} />}
          thanksgivingSub={<ThanksgivingTurkeySubCard url={pickUpHref} />}
          frenchDip={<FrenchDipCard url={pickUpHref} />}
        />
      ) : (
        <DatetimeShow
          after={montrealSmokedMeatCAStartDate}
          before={montrealSmokedMeatCAEndDate}
          default={
            <LtoContentCA
              thanksgivingSub={<ThanksgivingTurkeySubCard url={pickUpHref} />}
              default={null}
            />
          }
        >
          <MontrealSmokedMeatCard pickUpHref={pickUpHref} />
        </DatetimeShow>
      )}
      {region() === 'US' ? (
        <DatetimeShow
          after={firstRespondersMonthStartDate}
          before={firstRespondersMonthEndDate}
          default={<HotSauceBarCard />}
        >
          <FirstRespondersMonthCard pickUpHref={pickUpHref} />
        </DatetimeShow>
      ) : (
        <DatetimeShow
          after={firstRespondersMonthStartDate}
          before={firstRespondersMonthEndDate}
          default={
            <DatetimeShow
              after={hotSauceBarCanadaStartDate}
              before={hotSauceBarCanadaEndDate}
              default={<PublicSafetyFoundationCard foundationHref={foundationHref} />}
            >
              <HotSauceBarCanadaCard />
            </DatetimeShow>
          }
        >
          <FirstRespondersMonthCard pickUpHref={pickUpHref} />
        </DatetimeShow>
      )}
      <XStack>
        <CareersCard careersHref={careersHref} />
        <HookAndLadderCard pickUpHref={pickUpHref} />
      </XStack>
      {Boolean(cateringHref) && <CateringCard cateringHref={cateringHref} />}
    </>
  );
}

export function ScreenHomeDesktop(props: ScreenHomeProps) {
  const hasAlert = Boolean(props.alert?.text);

  return (
    <ScrollView contentContainerStyle={styles.wrapper}>
      <View style={styles.mainContent}>
        {hasAlert && <HomeAlert text={props.alert.text} link={props.alert.link} />}
        {props.userPointExpireAlert && props.userPointExpireAlert.text && (
          <HomeAlert
            text={props.userPointExpireAlert.text}
            link={props.userPointExpireAlert.link}
          />
        )}
        <HomeCard
          style={{ paddingHorizontal: 24, marginVertical: 24 }}
          displayedOrder={props.displayedOrder}
          displayedOrderCardLoading={props.displayedOrderCardLoading}
          redirectPath={props.redirectPath}
          onReorder={props.onReorder}
          reorderLoading={props.reorderLoading}
          logReorderCardPress={props.logReorderCardPress}
          isLoadingChallenges={props.isLoadingChallenges}
          loyaltyUserReady={props.loyaltyUserReady}
          isDesktop={props.isDesktop}
          challengeHomeCardProps={props.challengeHomeCardProps}
        />
        <MarketingCards
          cateringHref={props.cateringRedirectPath}
          pickUpHref={props.pickUpRedirectPath}
          deliveryHref={props.deliveryRedirectPath}
          foundationHref={props.foundationPath}
          careersHref={props.careersPath}
          donateHref={props.donatePath}
        />
      </View>
      <Footer />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    backgroundColor: tokens.colors.$white,
    flexGrow: 1,
  },
  orderDisplay: {
    marginVertical: 24,
    marginHorizontal: 32,
  },
  mainContent: {
    maxWidth: 1920,
    marginHorizontal: 'auto',
    width: '100%',
    flexGrow: 1,
  },
  horizontalHalfCardsStack: {
    justifyContent: 'space-between',
  },
});
